import { Controller } from "@hotwired/stimulus"

// Split presets card on /budgets/assign. A preset tap only does the maths:
// it is dispatched as budget-split:change and budget-assignment fills the
// Needs / Wants vials and holds Savings / Debt aside. Every vial stays
// editable; a hand edit sends the new split back (#sync).
//
// Which pill is pressed is the user's pick, never read off the numbers:
// custom is true from Custom until a preset is tapped, so Savings / Debt
// added in Custom stay Custom even when the vials happen to make 50/30/20.
// It rides the form as mode "custom" and is saved with the split.
export default class extends Controller {
  static targets = ["body", "preset", "custom", "field", "seg", "slot"]
  static values = { on: Boolean, custom: Boolean, need: Number, want: Number, savings: Number, debt: Number }

  connect() {
    this.render()
    this.#emit(true)
  }

  // Custom: the user's own amounts. From a preset it drops the split (nothing
  // set aside, the vials keep what they hold); in Custom it is already
  // pressed and does nothing, so cards added here are never wiped.
  custom() {
    if (this.customValue) return

    this.customValue = true
    this.onValue = false
    this.render()
    this.#emit()
  }

  // Turns the split on and fills the vials from it.
  preset(event) {
    this.customValue = false
    this.onValue = true
    this.#set(...event.currentTarget.dataset.split.split(",").map(Number))
    this.render()
    this.#emit()
  }

  // A Savings / Debt card added from the add sheet (Custom only): the split
  // turns on as budget-assignment read it off the vials, vials untouched.
  // Still Custom.
  adopt(event) {
    const { need, want, savings, debt } = event.detail
    this.onValue = true
    this.#set(need, want, savings, debt)
    this.render()
    this.dispatch("change", { detail: { on: true, ...this.#split(), initial: false, keepVials: true } })
  }

  // A Savings / Debt card removed with its x (Custom only): that slot drops
  // to 0, vials untouched. With no kept card left the split turns off.
  // Still Custom.
  drop(event) {
    const { on, need, want, savings, debt } = event.detail
    this.onValue = on
    this.#set(need, want, savings, debt)
    this.render()
    this.dispatch("change", { detail: { on, ...this.#split(), initial: false, keepVials: true } })
  }

  // Vials were edited by hand: adopt their split without re-filling them. A
  // preset edited off its numbers is the user's own plan now: Custom.
  sync(event) {
    const { need, want, savings, debt } = event.detail
    this.#set(need, want, savings, debt)
    if (!this.customValue && !this.#matchesPreset()) this.customValue = true
    this.render()
  }

  render() {
    const on = this.onValue

    // Bar, legend and kept cards appear once a split exists. The hidden
    // attribute, not the class: these elements set their own display.
    this.bodyTarget.querySelectorAll("[data-split-detail]").forEach((el) => { el.hidden = !on })

    this.fieldTargets.forEach((field) => {
      const key = field.dataset.key
      field.value = key === "mode" ? this.#mode() : this[`${key}Value`]
    })

    // Slots at 0% (Wants in 70/20/10, Debt in 50/30/20) drop out of the bar,
    // the legend and the kept-aside cards.
    const split = this.#split()
    this.segTargets.forEach((seg) => {
      seg.style.flexGrow = split[seg.dataset.key]
      seg.hidden = split[seg.dataset.key] === 0
    })
    this.slotTargets.forEach((slot) => { slot.hidden = split[slot.dataset.key] === 0 })
    document.querySelectorAll("[data-split-kept]").forEach((card) => {
      card.hidden = !on || split[card.dataset.splitKept] === 0
    })
    // A Needs / Wants budget Confirm will create is named as the tapped
    // preset's framework names it (80/20's is Spending); a Custom split is
    // saved with "custom", which matches no framework, so it gets 50/30/20's
    // names (BudgetFramework.slot_for).
    const current = Object.values(split).join(",")
    document.querySelectorAll("[data-split-name]").forEach((name) => {
      const preset = !this.customValue && JSON.parse(name.dataset.names)[current]
      name.textContent = preset || name.dataset.default
    })

    // The native + only belongs to Custom: dropping bridge--button sends
    // iOS its disconnect (item removed), restoring it re-adds the item.
    const custom = this.customValue
    const add = document.querySelector("[data-split-add]")
    if (custom) add?.setAttribute("data-controller", "bridge--button")
    else add?.removeAttribute("data-controller")

    // Clear all / Reset belong to Custom too. Invisible, not hidden: the
    // header row keeps its height so the card never resizes.
    document.querySelectorAll("[data-split-custom-only]").forEach((el) => el.classList.toggle("invisible", !custom))

    // Exactly one pill is pressed: Custom, or the preset the user tapped.
    this.presetTargets.forEach((pill) => pill.setAttribute("aria-pressed", !custom && pill.dataset.split === current))
    if (this.hasCustomTarget) this.customTarget.setAttribute("aria-pressed", custom)
  }

  // "own" (no split), "custom" (a split built in Custom) or "split" (a preset).
  #mode() {
    if (!this.onValue) return "own"
    return this.customValue ? "custom" : "split"
  }

  #matchesPreset() {
    const current = Object.values(this.#split()).join(",")
    return this.presetTargets.some((pill) => pill.dataset.split === current)
  }

  #split() {
    return { need: this.needValue, want: this.wantValue, savings: this.savingsValue, debt: this.debtValue }
  }

  #set(need, want, savings, debt = 0) {
    this.needValue = need
    this.wantValue = want
    this.savingsValue = savings
    this.debtValue = debt
  }

  // initial: the page just rendered with the saved limits; budget-assignment
  // records the split but must not overwrite those amounts.
  #emit(initial = false) {
    this.dispatch("change", {
      detail: { on: this.onValue, ...this.#split(), initial }
    })
  }
}
